"use client";
import React, { useState, useRef, useCallback, useEffect } from "react";
import { usePathname } from 'next/navigation';
import { Menu, X, ChevronDown, ChevronRight, Search } from "lucide-react";
import dynamic from 'next/dynamic';
import Image from "next/image";
import Link from "next/link";

// Centralized color management with full class names
interface MenuItem {
  id: number;
  title: string;
  link: string;
  submenu?: MenuItem[];
}

interface MenuItemProps {
  item: MenuItem;
}

const colors = {
  primary: "text-accent",
  secondary: "text-primary-light",
  hover: "hover:bg-hover",
  background: "bg-header-bg",
} as const;

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeMenus, setActiveMenus] = useState<number[]>([]);
  const headerRef = useRef<HTMLElement>(null);
  const pathname = usePathname();

  useEffect(() => {
    setIsOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!isOpen) {
      setActiveMenus([]);
    }
  }, [isOpen]);

  // Update header height whenever it changes
  const updateHeaderHeight = useCallback(() => {
    if (headerRef.current) {
      const height = headerRef.current.offsetHeight;
      document.documentElement.style.setProperty('--header-height', `${height}px`);
    }
  }, []);

  // Update height on menu toggle
  const toggleMenu = () => {
    setIsOpen(!isOpen);
    // Use setTimeout to wait for DOM update
    setTimeout(updateHeaderHeight, 0);
  };

  // Update height on window resize
  useEffect(() => {
    updateHeaderHeight();
    window.addEventListener('resize', updateHeaderHeight);
    return () => window.removeEventListener('resize', updateHeaderHeight);
  }, [updateHeaderHeight]);

  // Update height when active menus change
  useEffect(() => {
    updateHeaderHeight();
  }, [activeMenus, updateHeaderHeight]);

  const toggleSubmenu = (e: React.MouseEvent, menuId: number) => {
    e.preventDefault(); // Prevent any navigation
    e.stopPropagation();
    if (activeMenus.includes(menuId)) {
      setActiveMenus(activeMenus.filter((id) => id !== menuId));
    } else {
      setActiveMenus([...activeMenus, menuId]);
    }
  };

  const menuItems = [
    { id: 1, title: "ホーム", link: "/", submenu: [
      { id: 11, title: "プロフィール", link: "/nanamultilingual/" },
      {id: 22, title: "日記", link: "/tag/tagebuch"},
      {id: 23, title: "海外移住", link: "/category/reisen"},
    ] },
    {
      id: 2,
      title: "ドイツ語",
      link: "/deutschlernen",
      submenu: [
        {
          id: 21,
          title: "はじめ方",
          link: "/anfanger",
        },
        {
          id: 22,
          title: "単語",
          link: "/vokabeln-liste",
          submenu: [
            {
              id: 221,
              title: "辞書",
              link: "/aiueo-woerterbuch",
            },
          ],
        },
        {
          id: 23,
          title: "動詞",
          link: "/verben",
        },
        {
          id: 24,
          title: "表現",
          link: "/ausdruck",
          submenu: [
            {
              id: 241,
              title: "例文",
              link: "/satz-liste",
            },
            {
              id: 242,
              title: "多面性",
              link: "/vielseitig",
            },
          ],
        },
        {
          id: 25,
          title: "読解",
          link: "/tag/leseverstehen",
          submenu: [
            {
              id: 251,
              title: "作文",
              link: "/desutsch-uebungen",
            },
          ],
        },
      ],
    },
    {
      id: 3,
      title: "文法",
      link: "/grammatik-liste",
      submenu: [
        { id: 31, title: "文法一覧", link: "/grammatik-liste", submenu: [{ id: 211, title: "練習問題", link: "/deutsch-grammatik-ubungen" }] },
        { id: 32, title: "動詞", link: "/tag/verben" },
        { id: 33, title: "名詞", link: "/tag/substantive" },
        { id: 34, title: "冠詞", link: "/tag/artikel" },
        { id: 35, title: "代名詞", link: "/tag/pronomen" },
        { id: 36, title: "形容詞", link: "/tag/adjektive" },
        { id: 37, title: "接続詞", link: "/tag/konjunktionen" },
      ],
    },
    {
      id: 4,
      title: "レッスン",
      link: "/deutsch/lesson",
      submenu: [
        {
          id: 41,
          title: "カレンダー",
          link: "/kalender"
        }
      ]
    },
    {
      id: 5,
      title: "YouTube",
      link: "https://www.youtube.com/channel/UCpMUqywc4jMccMqyPGAwy3A",
      submenu: [
        {
          id: 51,
          title: "ポッドキャスト",
          link: "https://open.spotify.com/show/64VsqrvWNPz5fRDdMjHtWb"
        }
      ]
    },
  ];

  const NavbarSearch = dynamic(() => import('./search/NavbarSearch'), { ssr: false });

  const MenuItem: React.FC<MenuItemProps> = ({ item }) => {
    const hasSubmenu = item.submenu && item.submenu.length > 0;

    return (
      <li className="relative group">
        {hasSubmenu ? (
          <div className={`flex items-center w-full group cursor-pointer`}>
            <Link
              href={item.link}
              className={`flex-grow px-4 py-1 ${colors.primary} ${colors.hover} lg:group-hover:${colors.hover} flex items-center justify-between`}
              onClick={(e) => {
                // Prevent default link behavior only on mobile when the submenu is toggled
                if (window.innerWidth < 1024 && activeMenus.includes(item.id)) {
                  e.preventDefault();
                }
              }}
            >
              <span className="flex items-center gap-2">
                {item.id === -1 && <Search size={16} />}
                {item.title}
              </span>
              {/* Arrow button for mobile only */}
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  toggleSubmenu(e, item.id);
                }}
                className={`lg:hidden px-2 py-2 ${colors.primary} ${colors.hover}`}
              >
                <ChevronDown
                  size={16}
                  className={`transform ${activeMenus.includes(item.id) ? "rotate-180" : ""
                    } transition-transform`}
                />
              </button>
              {/* Arrow indicator for desktop (part of the Link) */}
              <div className="hidden lg:block px-2 py-2">
                <ChevronDown
                  size={16}
                  className={`transform lg:group-hover:rotate-180 transition-transform ${colors.primary}`}
                />
              </div>
            </Link>
          </div>
        ) : (
          <Link
            href={item.link}
            className={`flex items-center w-full px-4 py-2 ${colors.primary} ${colors.hover} lg:hover:${colors.hover}`}
          >
            <span>{item.title}</span>
          </Link>
        )}

        {hasSubmenu && (
          <ul
            className={`
              lg:absolute lg:left-0 lg:top-full lg:w-48 lg:shadow-md ${colors.background
              }
              ${activeMenus.includes(item.id) ? "block" : "hidden"} 
              lg:group-hover:block z-50
            `}
          >
            {(item.submenu || []).map((subItem) => (
              <li key={subItem.id} className="relative group/sub">
                {subItem.submenu ? (
                  <div
                    className={`flex items-center w-full group cursor-pointer`}
                  >
                    <Link
                      href={subItem.link}
                      className={`flex-grow pl-8 lg:pl-4 pr-4 py-2 ${colors.primary} ${colors.hover} lg:group-hover:${colors.hover} flex items-center justify-between`}
                      onClick={(e) => {
                        // Prevent default link behavior only on mobile when the submenu is toggled
                        if (
                          window.innerWidth < 1024 &&
                          activeMenus.includes(subItem.id)
                        ) {
                          e.preventDefault();
                        }
                      }}
                    >
                      <span>{subItem.title}</span>
                      {/* Arrow button for mobile only */}
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          toggleSubmenu(e, subItem.id);
                        }}
                        className={`lg:hidden px-2 py-2 ${colors.primary} ${colors.hover}`}
                      >
                        <ChevronRight size={16} />
                      </button>
                      {/* Arrow indicator for desktop (part of the Link) */}
                      <div className="hidden lg:block px-2 py-2">
                        <ChevronRight
                          size={16}
                          className={`${colors.primary}`}
                        />
                      </div>
                    </Link>
                  </div>
                ) : (
                  <Link
                    href={subItem.link}
                    className={`flex items-center w-full pl-8 lg:pl-4 pr-4 py-2 ${colors.primary} ${colors.hover}`}
                  >
                    <span>{subItem.title}</span>
                  </Link>
                )}

                {subItem.submenu && (
                  <ul
                    className={`
                      lg:absolute lg:left-full lg:top-0 lg:w-48 lg:shadow-md ${colors.background
                      }
                      ${activeMenus.includes(subItem.id) ? "block" : "hidden"}
                      lg:group-hover/sub:block z-50
                    `}
                  >
                    {(subItem.submenu || []).map((subSubItem) => (
                      <li key={subSubItem.id}>
                        <Link
                          href={subSubItem.link}
                          className={`flex items-center w-full pl-12 lg:pl-4 pr-4 py-2 ${colors.primary} ${colors.hover}`}
                        >
                          {subSubItem.title}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        )}
      </li>
    );
  };

  return (
    <header
      ref={headerRef}
      className={`${colors.background} bg-opacity-100 shadow-custom sticky top-0 z-50`}
    >
      <div className="max-w-5xl mx-auto px-2">
        <div className="flex items-center py-2 lg:justify-center relative">
          <div className="block lg:hidden">
            <button
              className={`p-2 ${colors.primary}`}
              onClick={toggleMenu}
              aria-label="Toggle menu"
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          <div className="flex justify-between flex-1 lg:flex-initial">
            <Link href="/" className="flex items-center">
              <div className="relative h-20 w-20">
                <Image
                  src="/logo.jpg"
                  alt="Logo"
                  sizes="6rem"
                  fill
                  className="object-contain"
                />
              </div>
              <div className="ml-4">
                <h1 className={`text-4xl font-bold ${colors.primary}`}>
                  言語こたつ
                </h1>
                <p className={`text-2x1 ${colors.secondary}`}>
                  Gengo-Kotatsu.com
                </p>
              </div>
            </Link>
            {/* Mobile search icon */}
            <div className="block lg:hidden">
              <Link
                href="/search"
                className={`p-2 ${colors.primary}`}
                aria-label="検索ページへ"
              >
                <Search size={24} />
              </Link>
            </div>
          </div>
        </div>

        <nav className={`${isOpen ? "block" : "hidden"} lg:block lg:pb-4`}>
          <ul className="lg:flex lg:justify-center lg:items-center lg:space-x-6">
            {menuItems.map((item) => (
              <MenuItem key={item.id} item={item} />
            ))}
            {/* Desktop search form */}
            <li className="hidden lg:block">
              <NavbarSearch />
            </li>
          </ul>
        </nav>
      </div>
    </header>
  );
};

export default Header;
